// Tooltip do MUI renomeado: o recharts também exporta Tooltip neste arquivo
import {
  Box,
  Paper,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatClockMinutes, type MetricSeries } from "../periodAggregation";
import type { PeriodChartStyle } from "./PeriodChartStyleToggle";

interface MetricTrendCardProps {
  series: MetricSeries;
  /** Período selecionado: 15/30 dias habilitam scroll horizontal. */
  rangeDays: number;
  /** Gráfico interno do cartão: linha (padrão) ou barras. */
  chartStyle?: PeriodChartStyle;
  /** Abre o dia clicado na visão detalhada. */
  onSelectDay: (dateKey: string) => void;
}

type ChartPoint = MetricSeries["points"][number] & { x: number };

/** Balão do gráfico: dia, valor, atividade/horário e dica de clique. */
function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    payload?: {
      dateLabel: string;
      formatted: string;
      activityLabel?: string;
    };
  }>;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {row.dateLabel}
      </Typography>
      {row.activityLabel ? (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, display: "block", mt: 0.15 }}
        >
          {row.activityLabel}
        </Typography>
      ) : null}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {row.formatted}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
      >
        Clique para ver este dia
      </Typography>
    </Box>
  );
}

/**
 * Rótulo DD/MM alinhado à coordenada do ponto (índice x).
 * Em 30 dias o Recharts omite ticks; usar index do tickFormatter desalinha a data.
 * Aqui o valor do tick É o índice do ponto — a bolinha e o dia batem.
 */
function DateAxisTick(props: {
  x?: number | string;
  y?: number | string;
  payload?: { value?: number | string };
  chartData: ChartPoint[];
  visibleX: Set<number>;
  fill: string;
}) {
  const { x, y, payload, chartData, visibleX, fill } = props;
  const dataIndex = Number(payload?.value);
  if (!Number.isFinite(dataIndex) || !visibleX.has(dataIndex)) {
    return null;
  }
  const label = chartData[dataIndex]?.dateLabel;
  if (!label || x == null || y == null) return null;

  const lastIndex = chartData.length - 1;
  // Evita corte horizontal: primeira data à esquerda, última à direita
  let textAnchor: "start" | "middle" | "end" = "middle";
  if (dataIndex === 0) textAnchor = "start";
  else if (dataIndex === lastIndex) textAnchor = "end";

  return (
    <text
      x={x}
      y={Number(y) + 4}
      dy={10}
      textAnchor={textAnchor}
      fontSize={10}
      fill={fill}
    >
      {label}
    </text>
  );
}

/**
 * Cartão de uma métrica do período: resumo + gráfico interno (linha ou barras).
 * Usa as mesmas séries de buildPeriodSeries (todas as métricas do FluxHealth).
 */
export function MetricTrendCard({
  series,
  rangeDays,
  chartStyle = "line",
  onSelectDay,
}: MetricTrendCardProps) {
  const theme = useTheme();
  const color = theme.palette.primary.main;
  const isLastSummary = series.summaryMode === "last";
  const isLine = chartStyle === "line";
  const isClockSeries =
    series.valueUnit === "clock" || series.valueCode === "clock";

  const formatAxisValue = (value: number) => {
    if (isClockSeries) return formatClockMinutes(value);
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return `${Math.round(value)}`;
  };

  // x = posição do ponto na série (0..n-1) — base do eixo e do rótulo
  const chartData = useMemo<ChartPoint[]>(
    () => series.points.map((point, x) => ({ ...point, x })),
    [series.points]
  );
  const hasHorizontalScroll = rangeDays === 15 || rangeDays === 30;
  const minimumChartWidth = rangeDays === 30 ? 1320 : 660;

  // Quais índices mostram DD/MM (sempre inclui primeiro e último)
  const visibleX = useMemo(() => {
    const n = chartData.length;
    if (n === 0) return new Set<number>();
    // No gráfico rolável há espaço para mostrar mais datas sem sobreposição.
    if (hasHorizontalScroll) {
      const indexes = new Set<number>();
      const step = rangeDays === 30 ? 2 : 1;
      for (let i = 0; i < n; i += step) indexes.add(i);
      indexes.add(0);
      indexes.add(n - 1);
      return indexes;
    }
    if (n <= 8) return new Set(chartData.map((point) => point.x));

    const maxLabels = 7;
    const step = (n - 1) / (maxLabels - 1);
    const indexes = new Set<number>();
    for (let i = 0; i < maxLabels; i += 1) {
      indexes.add(Math.round(i * step));
    }
    indexes.add(0);
    indexes.add(n - 1);
    return indexes;
  }, [chartData, hasHorizontalScroll, rangeDays]);

  const barColor = (value: number) => {
    if (series.average <= 0) return color;
    const ratio = value / series.average;
    if (ratio <= 0.6) return theme.palette.warning.main;
    if (ratio >= 1.15) return theme.palette.secondary.main;
    return color;
  };

  const handlePointClick = (data: any) => {
    const dateKey = data?.payload?.dateKey ?? data?.dateKey;
    if (dateKey) onSelectDay(dateKey);
  };

  const xAxis = (
    <XAxis
      dataKey="x"
      type="number"
      domain={[0, Math.max(chartData.length - 1, 0)]}
      ticks={chartData.map((point) => point.x)}
      interval={0}
      allowDecimals={false}
      niceTicks="none"
      axisLine={false}
      tickLine={false}
      height={36}
      tickMargin={4}
      tick={(tickProps) => (
        <DateAxisTick
          {...tickProps}
          chartData={chartData}
          visibleX={visibleX}
          fill={theme.palette.text.secondary}
        />
      )}
    />
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        // Impede que o gráfico largo aumente a coluna do grid.
        overflow: "hidden",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "neutral300.main",
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
      >
        <MuiTooltip title={series.label} placement="top" arrow>
          <Typography
            noWrap
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "1rem",
              minWidth: 0,
              flex: 1,
            }}
          >
            {series.label}
          </Typography>
        </MuiTooltip>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            flexShrink: 0,
            maxWidth: { xs: "48%", sm: "none" },
            textAlign: "right",
            lineHeight: 1.25,
            overflowWrap: "anywhere",
          }}
        >
          {isLastSummary
            ? "último registro"
            : series.sectionId === "exercise"
              ? `${series.points.length} ${
                  series.points.length === 1 ? "sessão" : "sessões"
                }`
              : `média de ${series.daysWithData} ${
                  series.daysWithData === 1 ? "dia" : "dias"
                }`}
        </Typography>
      </Stack>

      <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mt: 0.25 }}>
        {series.formattedAverage}
      </Typography>

      <Box
        sx={{
          mt: 1.25,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: hasHorizontalScroll ? "auto" : "hidden",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          // Garante que o gesto horizontal fique dentro do gráfico no mobile.
          overscrollBehaviorX: "contain",
          touchAction: hasHorizontalScroll ? "pan-x pan-y" : "auto",
        }}
        aria-label={
          hasHorizontalScroll
            ? `Gráfico de ${rangeDays} dias com rolagem horizontal`
            : undefined
        }
      >
        <Box
          sx={{
            height: { xs: 160, sm: 176 },
            width: hasHorizontalScroll
              ? `max(100%, ${minimumChartWidth}px)`
              : "100%",
            minWidth: 0,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            {isLine ? (
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 12, bottom: 8, left: 4 }}
              >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              {xAxis}
              <YAxis
                width={36}
                tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatAxisValue}
              />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2.25}
                connectNulls
                isAnimationActive={false}
                dot={(props: {
                  cx?: number;
                  cy?: number;
                  payload?: { dateKey?: string };
                }) => {
                  const { cx, cy, payload } = props;
                  if (cx == null || cy == null) return null;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill={color}
                      strokeWidth={0}
                      style={{ cursor: "pointer" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (payload?.dateKey) onSelectDay(payload.dateKey);
                      }}
                    />
                  );
                }}
                activeDot={(props: {
                  cx?: number;
                  cy?: number;
                  payload?: { dateKey?: string };
                }) => {
                  const { cx, cy, payload } = props;
                  if (cx == null || cy == null) return null;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      strokeWidth={0}
                      style={{ cursor: "pointer" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (payload?.dateKey) onSelectDay(payload.dateKey);
                      }}
                    />
                  );
                }}
              />
              </LineChart>
            ) : (
              <BarChart
                data={chartData}
                margin={{ top: 12, right: 12, bottom: 8, left: 4 }}
                barCategoryGap="18%"
              >
              {xAxis}
              <Tooltip
                cursor={{ fill: alpha(color, 0.08) }}
                content={<ChartTooltip />}
              />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
                maxBarSize={36}
                onClick={handlePointClick}
                style={{ cursor: "pointer" }}
              >
                {chartData.map((point) => (
                  <Cell key={point.pointId} fill={barColor(point.value)} />
                ))}
              </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
}
